<template>
  <div class="coupon-view">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <h5 class="text-semibold">{{ title }}</h5>
      <div class="d-flex align-items-center gap-3">
        <snow-button size="sm" text="Cancelar" variant="gray" @click="goBack" />
        <snow-button
          size="sm"
          text="Salvar"
          :loading="loading"
          :disabled="!isValid"
          @click="submit"
        />
      </div>
    </div>

    <snow-input
      v-model="form.code"
      id="code"
      required
      label="Nome do cupom"
      info="Defina o nome do cupom que seus clientes irão usar no checkout."
      :errors="errors.code"
      :limit="40"
    />

    <div class="row">
      <div class="col-lg-6 mb-lg-0 mb-4">
        <snow-input
          v-model="form.discount"
          money
          required
          label="Valor do cupom"
          info="Este valor não deve ser maior nem igual ao valor da oferta."
          :min="0"
          :errors="errors.discount"
        />
      </div>
      <div class="col-lg-6">
        <snow-input
          v-model="form.limit_max"
          mask="#########"
          label="Limite de usos"
          required
          info="Digite um número inteiro para o limite de usos do seu cupom."
          :min="1"
        />
      </div>
    </div>

    <h5 class="text-semibold mt-4">Definir período do cupon</h5>
    <div class="row">
      <div class="col-lg-6 mb-lg-0 mb-4">
        <snow-date-picker
          label="Início da validade do cupom"
          size="lg"
          v-model="form.start_at"
          format="YYYY-MM-DD HH:mm"
          isHours
        />
      </div>
      <div class="col-lg-6">
        <snow-date-picker
          label="Fim da validade do cupom"
          size="lg"
          v-model="form.end_at"
          format="YYYY-MM-DD HH:mm"
          isHours
        />
      </div>
    </div>

    <action-dispatcher
      :action="product.type_product == 'signature' ? 'plan/get' : 'offer/get'"
      :parameters="{ product_id: $route.params.id, limit: 100 }"
    >
      <snow-checkboxes-dropdown
        v-model="form.itens"
        required
        id="itens"
        :placeholder="
          product.type_product == 'signature'
            ? 'Selecionar planos'
            : 'Selecionar ofertas'
        "
        :options="formattedOptions"
      />
      <h6 class="text-dark-40 fs-12 mt-2">
        Selecione
        {{
          product.type_product == "signature"
            ? "os planos aos"
            : "as ofertas às"
        }}
        quais seu cupom se aplicará. Certifique-se de que o valor final no
        checkout, após aplicado o cupom, seja sempre maior que R$ 4,00.
      </h6>
    </action-dispatcher>
  </div>
</template>

<script>
import {
  clearObject,
  formatMoney,
  formFields,
  toComputerDate
} from "@/functions/helpers";
import { mapState } from "vuex";

export default {
  data() {
    return {
      ...formFields(
        [
          "id",
          "code",
          "product_id",
          "start_at",
          "end_at",
          "discount",
          "limit_max",
          "itens",
          "is_active"
        ],
        {
          product_id: this.$route.params.id,
          is_active: true,
          ...this.$route.params.coupon,
          itens: [
            ...(this.$route.params.coupon?.itens || []).map(
              i => i.offer_id || i.plan_id
            )
          ]
        }
      ),
      loading: false
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    ...mapState("offer", ["offers"]),
    ...mapState("plan", ["plans"]),
    title() {
      return this.form.id ? "Editar Cupom" : "Novo Cupom";
    },
    minEndDate() {
      if (this.form.start_at)
        return new Date(toComputerDate(this.form.start_at));

      return new Date();
    },
    formattedOptions() {
      const options =
        this.product.type_product === "signature" ? this.plans : this.offers;
      return options.map(option => ({
        id: option.id,
        label: option.name || "Sem nome"
      }));
    },
    isValid() {
      return (
        this.form.code &&
        this.form.code.length <= 40 &&
        this.form.start_at &&
        this.form.end_at &&
        this.form.limit_max > 0 &&
        !(this.form.limit_max % 1) &&
        this.form.discount > 0 &&
        !(this.form.discount % 1) &&
        this.form.itens.length
      );
    }
  },
  methods: {
    submit() {
      const data = { ...this.form };

      const action = data.id ? "product/editCoupon" : "product/postCoupon";

      if (data.itens.length) {
        const type =
          this.product.type_product == "signature" ? "plan_id" : "offer_id";
        data.itens = data.itens.map(i => ({ [type]: i }));
      }

      data.start_at = toComputerDate(data.start_at);
      data.end_at = toComputerDate(data.end_at);

      this.loading = true;
      this.$store
        .dispatch(action, clearObject(data))
        .then(() => {
          this.$message.success("Cupom salvo");
          this.goBack();
        })
        .catch(error => {
          this.$message.error(
            error.response?.data?.message || "Erro ao salvar cupom"
          );
        })
        .finally(() => (this.loading = false));
    },
    goBack() {
      this.$router.push({ name: "product-coupons" });
    }
  },
  watch: {
    "form.start_at"(v) {
      if (
        this.form.end_at &&
        new Date(toComputerDate(v)) > new Date(toComputerDate(this.form.end_at))
      ) {
        this.form.end_at = "";
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.coupon-view {
  background: var(--bg-2);
  padding: 24px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
